import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAdmin } from '../../contexts/AdminContext'
import { Company, Login } from '../../types/Subscription'
import Heading from '../../components/Heading'
import Text from '../../components/Text'
import Button from '../../components/Button'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import IconButton from '../../components/IconButton'
import CompanyDataForm from '../../compositions/Subscription/Forms/CompanyDataForm'
import LoginForm from '../../compositions/Subscription/Forms/LoginForm'
import { useTranslation } from 'react-i18next'
import { ValidationError } from 'yup'
import * as yup from 'yup'
import Card from '../../components/Card'
import { useNavigate } from 'react-router-dom'

const SubscriptionRegister: React.FC = () => {
    const { service } = useAdmin()
    const { t, i18n } = useTranslation(['settings'])
    const [data, setData] = useState<Partial<Company & Login>>({})
    const [showSuccess, setShowSuccess] = useState<boolean>(false)
    const [error, setError] = useState<string[]>([])
    const [showError, setShowError] = useState<boolean>(false)
    const navigate = useNavigate()

    const changeData = useCallback(
        (newData: Partial<Company & Login>) => {
            const dataWithLanguage: Partial<Company & Login> = {
                ...newData,
                language: i18n.language,
            }

            setData((prevData) => ({ ...prevData, ...dataWithLanguage }))
            setShowError(false)
        },
        [i18n]
    )

    const validation = useMemo(() => {
        return {
            email: yup
                .string()
                .email(t('validation.email'))
                .required(
                    t('validation.required', {
                        de: 'Die E-Mail',
                        en: 'password',
                    })
                ),
            password: yup
                .string()
                .required(
                    t('validation.required', {
                        de: 'Das Passwort',
                        en: 'password',
                    })
                )
                .min(
                    6,
                    t('validation.min', {
                        de: 'Das Passwort',
                        en: 'password',
                        number: 6,
                    })
                ),
            company: yup
                .string()
                .required(
                    t('validation.required', {
                        de: 'Der Firmenname',
                        en: 'company',
                    })
                )
                .min(
                    3,
                    t('validation.min', {
                        de: 'Der Firmenname',
                        en: 'password',
                        number: 3,
                    })
                ),
            firstname: yup
                .string()
                .required(
                    t('validation.required', {
                        de: 'Der Vorname',
                        en: 'firstname',
                    })
                )
                .min(
                    3,
                    t('validation.min', {
                        de: 'Der Vorname',
                        en: 'password',
                        number: 3,
                    })
                ),
            lastname: yup
                .string()
                .required(
                    t('validation.required', {
                        de: 'Der Nachname',
                        en: 'lastname',
                    })
                )
                .min(
                    3,
                    t('validation.min', {
                        de: 'Der Vorname',
                        en: 'password',
                        number: 3,
                    })
                ),
            street: yup
                .string()
                .required(
                    t('validation.required', {
                        de: 'Der Straßenname',
                        en: 'street',
                    })
                )
                .min(
                    3,
                    t('validation.min', {
                        de: 'Der Straßenname',
                        en: 'password',
                        number: 3,
                    })
                ),
            zipcode: yup
                .string()
                .required(
                    t('validation.required', {
                        de: 'Die Postleitzahl',
                        en: 'firstname',
                    })
                )
                .min(
                    3,
                    t('validation.min', {
                        de: 'Die Postleitzahl',
                        en: 'password',
                        number: 3,
                    })
                ),
            city: yup
                .string()
                .required(
                    t('validation.required', { de: 'Der Ort', en: 'city' })
                )
                .min(
                    3,
                    t('validation.min', {
                        de: 'Der Ort',
                        en: 'password',
                        number: 3,
                    })
                ),
            agb: yup
                .boolean()
                .required(t('validation.isTrue', { de: 'der AGBs', en: 'agb' }))
                .oneOf(
                    [true],
                    t('validation.isTrue', {
                        de: 'der AGBs',
                        en: 'terms and conditions',
                    })
                ),
            disclaimer: yup
                .boolean()
                .required(
                    t('validation.isTrue', {
                        de: 'der Datenschutzerklärung',
                        en: 'disclaimer',
                    })
                )
                .oneOf(
                    [true],
                    t('validation.isTrue', {
                        de: 'der Datenschutzerklärung',
                        en: 'disclaimer',
                    })
                ),
        }
    }, [t])

    useEffect(() => {
        yup.object(validation)
            .validate(data, {
                abortEarly: false,
            })
            .then(() => {
                setError([])
            })
            .catch((reason) => {
                setError(
                    reason.inner.map((e: ValidationError) => {
                        return e.message
                    })
                )
            })
    }, [data, validation, t])

    const submit = useCallback(() => {
        if (error.length === 0) {
            service.register(data).then((res) => {
                if (res.status === 201) {
                    setShowSuccess(true)
                }
            })
        } else {
            setShowError(true)
            window.scrollTo({
                top: 200,
                behavior: 'smooth',
            })
        }
    }, [service, data, error])

    return (
        <div className={'zr-mt-3'}>
            {!showSuccess ? (
                <>
                    <Heading level={3} classes={'zr-my-4'}>
                        {t('subscription.registerInTheSystem')}
                    </Heading>
                    <IconButton
                        icon={faArrowLeft}
                        color={'black'}
                        onClick={() => navigate('/subscription')}
                    >
                        {t('subscription.back')}
                    </IconButton>
                    <Text classes={'zr-mt-3'}>
                        {t('subscription.alreadyHasPlugIn')}
                    </Text>
                    {showError && (
                        <Card
                            style={{ background: '#FFCC0033' }}
                            classes={'zr-my-4'}
                        >
                            <ul>
                                {error.map((e) => {
                                    return (
                                        <li key={e}>
                                            <small>{e}</small>
                                        </li>
                                    )
                                })}
                            </ul>
                        </Card>
                    )}
                    <LoginForm data={data} changeData={changeData} />
                    <CompanyDataForm data={data} changeData={changeData} />
                    <div className={'zr-d-flex zr-justify-end zr-mb-3'}>
                        <Button
                            fn={() => submit()}
                            hierarchy={'primary'}
                            text={'Registrieren'}
                        />
                    </div>
                </>
            ) : (
                <div className={'zr-my-6'}>
                    <Heading level={3}>
                        {t('subscription.registrationSuccess')}
                    </Heading>
                    <div className={'zr-my-6'}>
                        <Button
                            hierarchy={'secondary'}
                            text={t('subscription.login')}
                            fn={() => navigate('/subscription/login')}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default SubscriptionRegister
